import type { DockviewApi, Parameters as DockviewParameters } from "dockview"
import type { DimensionValue, OPDockviewPanelDefinition, PanelDimensionRange } from "./types"

interface ContainerDimensions {
  width: number
  height: number
}

type ResolveDimensionInput = { unit: "pixel", value: number } | { unit: "percent", value: string, containerSize: number }

const resolveDimensionValue = (input: ResolveDimensionInput): number => {
  if (input.unit === "pixel") return input.value
  return Math.round((input.containerSize * parseFloat(input.value)) / 100)
}

const resolveDimensionRange = (range: PanelDimensionRange | undefined, containerSize: number): { min?: number, max?: number } => {
  if (!range) return {}

  const resolveValue = (dimension: DimensionValue | undefined): number | undefined => {
    if (!dimension) return undefined
    if (typeof dimension === "number") return resolveDimensionValue({ unit: "pixel", value: dimension })
    return resolveDimensionValue({ unit: "percent", value: dimension, containerSize })
  }

  return {
    min: resolveValue(range.min),
    max: resolveValue(range.max),
  }
}

export function resolvePanelDefinition<P extends DockviewParameters>(
  panelDefinition: OPDockviewPanelDefinition<P>,
  containerDimensions: ContainerDimensions,
): OPDockviewPanelDefinition<P> {
  const { min: minimumWidth, max: maximumWidth } = resolveDimensionRange(panelDefinition.widthRange, containerDimensions.width)
  const { min: minimumHeight, max: maximumHeight } = resolveDimensionRange(panelDefinition.heightRange, containerDimensions.height)

  return {
    ...panelDefinition,
    minimumWidth,
    maximumWidth,
    minimumHeight,
    maximumHeight,
  }
}

/**
 * Applies a complete set of panel definitions by clearing the layout and recreating all panels.
 * Use this for initial layout creation, manual resets, and error recovery.
 * For incremental updates, use applyPanelChanges instead.
 *
 * @param api - Dockview API instance
 * @param panels - Complete set of panel definitions to apply
 */
export function applyPanelDefinitions(api: DockviewApi, panels: readonly OPDockviewPanelDefinition[]): void {
  api.clear()

  const containerDimensions: ContainerDimensions = {
    width: api.width,
    height: api.height,
  }

  panels.forEach((panelDefinition, index) => {
    const resolvedDefinition = resolvePanelDefinition(panelDefinition, containerDimensions)
    const panel = api.addPanel(resolvedDefinition)

    if (index === 0) {
      panel.api.setActive()
    }
  })
}

/**
 * Panel changes for incremental updates
 */
export interface PanelChanges {
  /** Panel definitions to add */
  added: OPDockviewPanelDefinition[]
  /** Panel IDs to remove */
  removed: string[]
}

/**
 * Applies incremental panel changes without destroying the existing layout.
 * Preserves user's layout customizations while adding/removing specific panels.
 *
 * Strategy for adding panels:
 * - If panels exist: Stack new panels as tabs with the first panel
 * - If no panels exist: Add as the first panel
 *
 * @param api - Dockview API instance
 * @param changes - Panel additions and removals to apply
 *
 */
export function applyPanelChanges(
  api: DockviewApi,
  changes: PanelChanges,
): void {
  const { added, removed } = changes

  // Remove panels that no longer exist
  removed.forEach(panelId => {
    const panel = api.getPanel(panelId)
    if (panel) {
      api.removePanel(panel)
    }
  })

  // Add new panels without clearing existing ones
  const containerDimensions: ContainerDimensions = {
    width: api.width,
    height: api.height,
  }

  // Capture reference panel before loop to ensure consistent grouping
  // (api.panels may update synchronously during addPanel calls)
  const referencePanel = api.panels[0]

  added.forEach(panelDef => {
    if (api.getPanel(panelDef.id)) {
      return
    }
    const resolved = resolvePanelDefinition(panelDef, containerDimensions)

    // Strategy: Stack new panels with first existing panel as tabs
    if (referencePanel) {
      // Add panel positioned relative to reference panel
      api.addPanel({
        ...resolved,
        position: {
          referencePanel: referencePanel.id,
          direction: "within" as const, // Stack as tab in same group
        },
        floating: false, // Ensure not floating
      })
    } else {
      // No existing panels, add as first
      api.addPanel(resolved)
    }
  })
}
